// 🚦 Enforced Tracking System
// Combines auto-tracking with subscription limit enforcement

import { 
  setGlobalCompanyId, 
  getCurrentCompanyId, 
  getDoc as trackedGetDoc,
  getDocs as trackedGetDocs,
  setDoc as trackedSetDoc,
  updateDoc as trackedUpdateDoc,
  deleteDoc as trackedDeleteDoc,
  addDoc as trackedAddDoc,
  writeBatch as trackedWriteBatch,
  doc,
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  increment,
  serverTimestamp,
  onSnapshot
} from './autoTrackingFirebase';

import { enforceFeatureLimit } from './subscriptionLimits';

// Track active enforcement
let enforcementEnabled = true;

/**
 * 🔐 Enable/Disable enforcement
 */
export const setEnforcementEnabled = (enabled) => {
  enforcementEnabled = enabled;
  console.log(`🚦 Subscription enforcement ${enabled ? 'enabled' : 'disabled'}`);
};

/**
 * 📋 Map collection names to feature keys
 */
const COLLECTION_FEATURE_MAP = {
  // Sales collections
  'invoices': 'invoices',
  'sales_invoices': 'invoices',
  'salesBills': 'invoices', // Main sales bills collection (invoices, challans, quotations)
  'quotations': 'quotations',
  'quotation': 'quotations',
  'challans': 'challans',
  'challan': 'challans',
  'delivery_challans': 'challans',
  'delivery_challan': 'challans',
  'sales_credit_notes': 'sales_credit_notes',
  'sales_debit_notes': 'sales_debit_notes',
  
  // Purchase collections
  'purchase_bills': 'bills',
  'purchaseBills': 'bills', // Main purchase bills collection
  'bills': 'bills',
  'purchase_orders': 'purchase_orders',
  'purchaseOrders': 'purchase_orders', // Main purchase orders collection
  'purchase_order': 'purchase_orders',
  'purchase_receipts': 'purchase_receipts',
  'purchase_credit_notes': 'purchase_credit_notes',
  'purchase_debit_notes': 'purchase_debit_notes',
  
  // Payment collections
  'payment_receipts': 'payment_receipts',
  'payments': 'payments',
  'refunds': 'refunds',
  
  // Party collections
  'parties': 'customers', // Main parties collection (customers, suppliers)
  'customers': 'customers',
  'suppliers': 'suppliers',
  'employees': 'employees',
  
  // Report collections
  'gst_reports': 'gst_reports',
  'financial_reports': 'financial_reports',
  'custom_reports': 'custom_reports',
  'exports': 'exports'
};

/**
 * When a shared collection (e.g. salesBills) stores multiple doc types,
 * refine the feature key from document fields.
 */
function refineFeatureKeyFromData(featureKey, data) {
  if (!featureKey || !data || typeof data !== 'object') return featureKey;
  const t = String(data.type || data.billType || data.documentType || data.docType || '').toLowerCase();
  if (!t) return featureKey;

  if (featureKey === 'invoices') {
    if (t.includes('challan')) return 'challans';
    if (t.includes('quotation') || t.includes('quote')) return 'quotations';
  }
  return featureKey;
}

function createLimitCheckFailureError(originalError) {
  const failClosed = new Error(
    'Unable to verify subscription limits. Please check your connection and try again.'
  );
  failClosed.code = 'SUBSCRIPTION_CHECK_FAILED';
  failClosed.cause = originalError;
  return failClosed;
}

/**
 * 🔍 Extract feature key from document reference
 */
function getFeatureKeyFromRef(docRef) {
  try {
    // Get collection path from reference
    const path = docRef.path;
    const parts = path.split('/');
    
    // Find the collection name (usually the parent of the document)
    for (let i = parts.length - 1; i >= 0; i--) {
      const part = parts[i];
      const featureKey = COLLECTION_FEATURE_MAP[part];
      if (featureKey) {
        return featureKey;
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error extracting feature key:', error);
    return null;
  }
}

/**
 * 🔍 Extract feature key from collection reference
 */
function getFeatureKeyFromCollection(collectionRef) {
  try {
    const path = collectionRef.path;
    const parts = path.split('/');
    const collectionName = parts[parts.length - 1];
    
    return COLLECTION_FEATURE_MAP[collectionName] || null;
  } catch (error) {
    console.error('Error extracting feature key from collection:', error);
    return null;
  }
}

/**
 * ✍️ Enforced setDoc - Checks limits before write
 */
export const enforcedSetDoc = async (docRef, data, options = {}) => {
  const companyId = getCurrentCompanyId();
  let featureKey = refineFeatureKeyFromData(getFeatureKeyFromRef(docRef), data);
  
  // If enforcement enabled and we have both company and feature
  if (enforcementEnabled && companyId && featureKey && !options.skipEnforcement) {
    try {
      // Check if operation is allowed (write operation)
      const limitCheck = await enforceFeatureLimit(companyId, featureKey, true, 'write');
      
      if (!limitCheck.allowed) {
        const error = new Error(`Plan limit reached: Cannot create new ${limitCheck.featureName}. Please upgrade your plan.`);
        error.code = 'PLAN_LIMIT_REACHED';
        error.limitCheck = limitCheck;
        throw error;
      }
    } catch (error) {
      // Block operation for subscription/limit errors
      if (error.code === 'PLAN_LIMIT_REACHED' || 
          error.code === 'SUBSCRIPTION_EXPIRED' || 
          error.code === 'FEATURE_LIMIT_EXCEEDED' ||
          error.code === 'SUBSCRIPTION_CHECK_FAILED') {
        throw error;
      }
      // Fail closed: do not allow writes when limits cannot be verified
      console.error('⚠️ Limit check failed — blocking write (fail-closed):', error);
      throw createLimitCheckFailureError(error);
    }
  }
  
  // Proceed with tracked operation
  return trackedSetDoc(docRef, data, options);
};

/**
 * ➕ Enforced addDoc - Checks limits before adding
 */
export const enforcedAddDoc = async (collectionRef, data, options = {}) => {
  const companyId = getCurrentCompanyId();
  let featureKey = refineFeatureKeyFromData(getFeatureKeyFromCollection(collectionRef), data);
  
  // If enforcement enabled and we have both company and feature
  if (enforcementEnabled && companyId && featureKey && !options.skipEnforcement) {
    try {
      // Check if operation is allowed (write operation)
      const limitCheck = await enforceFeatureLimit(companyId, featureKey, true, 'write');
      
      if (!limitCheck.allowed) {
        const error = new Error(`Plan limit reached: Cannot create new ${limitCheck.featureName}. Please upgrade your plan.`);
        error.code = 'PLAN_LIMIT_REACHED';
        error.limitCheck = limitCheck;
        throw error;
      }
      
    } catch (error) {
      // Block operation for subscription/limit errors
      if (error.code === 'PLAN_LIMIT_REACHED' || 
          error.code === 'SUBSCRIPTION_EXPIRED' || 
          error.code === 'FEATURE_LIMIT_EXCEEDED' ||
          error.code === 'SUBSCRIPTION_CHECK_FAILED') {
        throw error;
      }
      console.error('⚠️ Limit check failed — blocking write (fail-closed):', error);
      throw createLimitCheckFailureError(error);
    }
  }
  
  // Proceed with tracked operation
  return trackedAddDoc(collectionRef, data, options);
};

/**
 * 🔄 Enforced updateDoc - Checks limits before update
 */
export const enforcedUpdateDoc = async (docRef, data, options = {}) => {
  const companyId = getCurrentCompanyId();
  let featureKey = refineFeatureKeyFromData(getFeatureKeyFromRef(docRef), data);
  
  // If enforcement enabled and we have both company and feature
  if (enforcementEnabled && companyId && featureKey && !options.skipEnforcement) {
    try {
      // Check if operation is allowed (write operation)
      const limitCheck = await enforceFeatureLimit(companyId, featureKey, true, 'write');
      
      if (!limitCheck.allowed) {
        const error = new Error(`Plan limit reached: Cannot update ${limitCheck.featureName}. Please upgrade your plan.`);
        error.code = 'PLAN_LIMIT_REACHED';
        error.limitCheck = limitCheck;
        throw error;
      }
    } catch (error) {
      // Block operation for subscription/limit errors
      if (error.code === 'PLAN_LIMIT_REACHED' || 
          error.code === 'SUBSCRIPTION_EXPIRED' || 
          error.code === 'FEATURE_LIMIT_EXCEEDED' ||
          error.code === 'SUBSCRIPTION_CHECK_FAILED') {
        throw error;
      }
      console.error('⚠️ Limit check failed — blocking write (fail-closed):', error);
      throw createLimitCheckFailureError(error);
    }
  }
  
  // Proceed with tracked operation
  return trackedUpdateDoc(docRef, data, options);
};

/**
 * 🗑️ Enforced deleteDoc - Checks limits before delete
 */
export const enforcedDeleteDoc = async (docRef, options = {}) => {
  const companyId = getCurrentCompanyId();
  const featureKey = getFeatureKeyFromRef(docRef);
  
  // If enforcement enabled and we have both company and feature
  if (enforcementEnabled && companyId && featureKey && !options.skipEnforcement) {
    try {
      // Check if operation is allowed (write operation)
      const limitCheck = await enforceFeatureLimit(companyId, featureKey, true, 'write');
      
      if (!limitCheck.allowed) {
        const error = new Error(`Plan limit reached: Cannot delete ${limitCheck.featureName}. Please upgrade your plan.`);
        error.code = 'PLAN_LIMIT_REACHED';
        error.limitCheck = limitCheck;
        throw error;
      }
      
    } catch (error) {
      // Block operation for subscription/limit errors
      if (error.code === 'PLAN_LIMIT_REACHED' || 
          error.code === 'SUBSCRIPTION_EXPIRED' || 
          error.code === 'FEATURE_LIMIT_EXCEEDED' ||
          error.code === 'SUBSCRIPTION_CHECK_FAILED') {
        throw error;
      }
      console.error('⚠️ Limit check failed — blocking delete (fail-closed):', error);
      throw createLimitCheckFailureError(error);
    }
  }
  
  // Proceed with tracked operation
  return trackedDeleteDoc(docRef, options);
};

/**
 * 📖 Read operations - Always allowed (even when subscription expired)
 * These operations don't modify data, so we allow them for better UX
 */
export const enforcedGetDoc = trackedGetDoc;
export const enforcedGetDocs = trackedGetDocs;

/**
 * 📦 Re-export utility functions
 */
export {
  setGlobalCompanyId,
  getCurrentCompanyId,
  trackedWriteBatch as writeBatch,
  doc,
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  increment,
  serverTimestamp,
  onSnapshot
};

/**
 * 🎯 Simple enforcement wrapper for any operation
 * Use this to wrap operations that need limit checking
 */
export async function withEnforcement(companyId, featureKey, operation, throwOnBlock = true) {
  if (!enforcementEnabled) {
    return await operation();
  }
  
  try {
    const limitCheck = await enforceFeatureLimit(companyId, featureKey, throwOnBlock, 'write');
    
    if (!limitCheck.allowed) {
      if (throwOnBlock) {
        const error = new Error(`Plan limit reached for ${limitCheck.featureName}`);
        error.code = 'PLAN_LIMIT_REACHED';
        error.limitCheck = limitCheck;
        throw error;
      }
      
      return {
        success: false,
        blocked: true,
        limitCheck
      };
    }
    
    // Execute operation
    const result = await operation();
    
    return {
      success: true,
      blocked: false,
      result,
      limitCheck
    };
    
  } catch (error) {
    // Block operation for subscription/limit errors
    if (error.code === 'PLAN_LIMIT_REACHED' || 
        error.code === 'SUBSCRIPTION_EXPIRED' || 
        error.code === 'FEATURE_LIMIT_EXCEEDED') {
      throw error;
    }
    throw error;
  }
}

export default {
  // Enforced operations
  setDoc: enforcedSetDoc,
  addDoc: enforcedAddDoc,
  updateDoc: enforcedUpdateDoc,
  deleteDoc: enforcedDeleteDoc,
  getDoc: enforcedGetDoc,
  getDocs: enforcedGetDocs,
  
  // Utilities
  setGlobalCompanyId,
  getCurrentCompanyId,
  setEnforcementEnabled,
  withEnforcement,
  
  // Re-exports
  writeBatch: trackedWriteBatch,
  doc,
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  increment,
  serverTimestamp,
  onSnapshot
};

